/**
 * BarList — ranked horizontal-bar list (e.g. "Top 10 reporters") used on
 * tenant + global dashboards. Sorts items descending and renders rank +
 * label + bar + value in a tight 4-column grid.
 */
import { cn } from './cn';

export interface BarListItem {
  /** Visible label, e.g. "google.com". */
  label: string;
  /** Numeric value; bars are scaled relative to the max in the dataset. */
  value: number;
}

export interface BarListProps {
  items: readonly BarListItem[];
  /** Max number of rows to render; defaults to 10. */
  max?: number;
  /** Optional custom value formatter (defaults to nl-BE thin-space groups). */
  formatValue?: (value: number) => string;
  className?: string;
}

const NL = new Intl.NumberFormat('nl-BE');
const defaultFormat = (n: number): string => NL.format(n).replace(/\./g, ' ');

export function BarList({
  items,
  max = 10,
  formatValue = defaultFormat,
  className,
}: BarListProps): JSX.Element {
  const sorted = [...items].sort((a, b) => b.value - a.value).slice(0, max);
  const peak = sorted.reduce((acc, it) => (it.value > acc ? it.value : acc), 0) || 1;
  return (
    <ol className={cn('space-y-1.5', className)}>
      {sorted.map((item, i) => (
        <li
          key={`${item.label}-${i}`}
          className="grid grid-cols-[16px_minmax(0,1fr)_minmax(80px,140px)_60px] items-center gap-3 text-xs"
        >
          <span className="text-right font-mono text-xs text-fg-disabled">{i + 1}</span>
          <span className="truncate text-fg-primary">{item.label}</span>
          <span className="h-1.5 overflow-hidden rounded bg-slate-100">
            <span
              className="block h-full bg-slate-500"
              style={{ width: `${(item.value / peak) * 100}%` }}
            />
          </span>
          <span className="text-right font-mono tabular-nums text-fg-secondary">
            {formatValue(item.value)}
          </span>
        </li>
      ))}
    </ol>
  );
}
